import React from 'react'

import cx from 'classnames'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { applyOpacity } from '@helpers/styled-helpers'

import { FilterOptions } from '@components/filters/elements'
import {
  Button,
  FilterClearIconButton,
  FilterSearchIcon,
  FlexRow,
  Input,
  InputLabel,
  List,
  LIST_ITEM_CSS_VARIABLES,
  ListItem,
  TextField,
} from '@components/ui'
import { Checkbox } from '@components/ui/Checkbox'
import { Typography } from '@components/ui/Typography'

import { FilterFieldLabel } from '../FilterFieldLabel'
import { usePlaceholderMessage } from './usePlaceholderMessage'
import { useSearchFilter } from './useSearchFilter'

import { SearchLabelMessage } from '@messages'
import { FilterFormFormControlLabel, FilterWidgetFormControlLabel } from '@components/filters/PageFilterBars/styles'

const MenuHeadingTypography = styled(Typography)`
  margin-bottom: 10px;
  text-transform: uppercase;

  label + & {
    margin-top: 10px;
  }
`

const SearchOptionsWrapper = styled.div`
  margin-top: 20px;
  display: grid;
  gap: 10px 20px;
  grid-template-columns: repeat(4, 1fr);
`

const StyledFlexRow = styled(FlexRow)`
  justify-content: space-between;
`

const StyledInput = styled(Input)`
  border: none;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray[100]};
  padding: 5px;
  background-color: ${({ theme }) => theme.colors.gray[0]};
  font-weight: 700;
  height: 30px;
  margin-left: -5px;
  margin-bottom: -5px;
  flex: 1;

  :focus-within,
  :hover {
    background-color: ${({ theme }) => theme.colors.common.oceanBlue}10;
  }

  input {
    height: 28px;
  }
  input::placeholder {
    font-style: italic;
    font-weight: 500;
  }
  input:placeholder-shown {
    text-overflow: ellipsis;
  }
`

const StyledFilterSearchIcon = styled(FilterSearchIcon)`
  color: ${({ theme }) => theme.colors.gray[100]};
`

const StyledInputLabel = styled(InputLabel)`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray[70]};
  text-transform: uppercase;
  margin-bottom: 0;
`

const StyledMenuList = styled(List).attrs({
  menu: true,
})`
  ${LIST_ITEM_CSS_VARIABLES.listItemVerticalPadding}: 5px;
  ${LIST_ITEM_CSS_VARIABLES.listItemHorizontalPadding}: 20px;
  ${LIST_ITEM_CSS_VARIABLES.menuItemHoverColor}: ${({ theme }) => applyOpacity(theme.colors.blue[40], 6)};
  ${LIST_ITEM_CSS_VARIABLES.listItemTextColor}: ${({ theme }) => theme.colors.gray[100]};

  border-top: 1px solid ${({ theme }) => theme.colors.common.paperStroke};

  & > li {
    height: 30px;
    display: flex;
    align-items: center;
    box-sizing: border-box;
  }
`

const SearchButton = styled(Button)`
  padding: 5px;
  margin-right: -5px;
  min-width: 0;
  color: ${({ theme }) => theme.colors.common.oceanBlue};
  height: 30px;
  font-size: 12px;
  border-radius: 4px;
  font-weight: 400;

  &:hover {
    background-color: ${({ theme }) => applyOpacity(theme.colors.common.oceanBlue, 6)};
  }
`

const SearchFilterRoot = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  flex-shrink: 0;
  height: 100%;
  padding: 15px 20px;
`

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: -5px;
  height: 30px;
  flex-shrink: 0;
`

const MenuContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  min-width: 275px;
`

const SearchTooltipText = (
  <FormattedMessage
    id="filterBar.searchFilter.tooltip.info"
    defaultMessage="A Keresés szűrői között “VAGY” kapcsolat van. Az eredmények között minden megjelenik, amire legalább egy feltétel igaz."
  />
)

const commonFilterFieldLabelProps = {
  tooltipText: SearchTooltipText,
  label: SearchLabelMessage,
}

interface FormSearchFilterProps {
  className?: string
  disabled?: boolean
  onChange: (payload: Pick<CommonSearchFilters, 'search'> | Pick<CommonSearchFilters, 'searchFields'>) => void
  onInputValueChange?: (payload: CommonSearchFilters['search']) => void
  search: CommonSearchFilters['search']
  searchFieldExtraOptions?: SearchFilterOption[]
  searchFieldOptions: SearchFilterOption[]
  searchFields: CommonSearchFilters['searchFields']
}

export function FormSearchFilter({
  className,
  onChange,
  onInputValueChange,
  search,
  searchFieldExtraOptions = [],
  searchFieldOptions,
  searchFields,
}: Exclude<FormSearchFilterProps, 'disabled'>) {
  const { value, inputPlaceholder, handleInputChange, handleInputClear, handleEscClear, handleChange } =
    useSearchFilter({
      debounce: false,
      onChange,
      onInputValueChange,
      search,
      searchFields,
      searchFieldOptions,
      searchFieldExtraOptions,
    })

  // We wish to display the single option as a disabled and checked option
  const isSingleOptionSearch = searchFieldOptions.length === 1

  return (
    <div className={className}>
      <TextField
        data-testid="search-filter"
        className="form-control"
        label={<FilterFieldLabel {...commonFilterFieldLabelProps} />}
        name="search"
        onChange={handleInputChange}
        onKeyDown={handleEscClear}
        endAdornment={
          <>
            <FilterClearIconButton onClick={handleInputClear} visible={Boolean(search)} />
            <FilterSearchIcon size={18} />
          </>
        }
        placeholder={inputPlaceholder}
        value={value}
      />

      <SearchOptionsWrapper>
        {[...searchFieldOptions, ...searchFieldExtraOptions].map(option => {
          const isOnlySelected = searchFields.includes(option.key) && searchFields.length === 1
          return (
            <FilterFormFormControlLabel
              key={option.key}
              disabled={isSingleOptionSearch || isOnlySelected}
              checked={isSingleOptionSearch || searchFields.includes(option.key)}
              name={option.key}
              onChange={handleChange}
              control={<Checkbox className={cx({ onlySelected: isOnlySelected })} />}
              label={option.label}
              gap={10}
            />
          )
        })}
      </SearchOptionsWrapper>
    </div>
  )
}

interface WidgetSearchFilterProps extends FormSearchFilterProps {
  searchFieldPresets?: SearchFilterPreset[]
}
/**
 *  SearchFilter used in the PageFilterBar when searching in the table
 *
 * @param {WidgetSearchFilterProps} {
 *   disabled = false,
 *   onChange,
 *   onInputValueChange
 *   search,
 *   searchFieldExtraOptions,
 *   searchFieldOptions,
 *   searchFieldPresets,
 *   searchFields,
 * }
 */
export function WidgetSearchFilter({
  disabled = false,
  onChange,
  onInputValueChange,
  search,
  searchFieldExtraOptions = [],
  searchFieldOptions,
  searchFieldPresets = [],
  searchFields,
}: WidgetSearchFilterProps) {
  const [open, setOpen] = React.useState(false)

  const {
    value,
    inputPlaceholder,
    handleInputChange,
    handleInputClear,
    handleEscClear,
    handleChange,
    handlePresetClick,
    searchSelectionState,
  } = useSearchFilter({
    debounce: true,
    onChange,
    onInputValueChange,
    search,
    searchFields,
    searchFieldOptions,
    searchFieldExtraOptions,
  })

  // We wish to display the single option as a disabled and checked option
  const isSingleOptionSearch = searchFieldOptions.length + searchFieldExtraOptions.length === 1

  const placeholderMessage = usePlaceholderMessage(searchSelectionState, isSingleOptionSearch)

  function onOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)
  }

  function handleOpen() {
    setOpen(true)
  }

  return (
    <SearchFilterRoot data-testid="widget-search-filter">
      <TopRow>
        <StyledInputLabel htmlFor="searchInput">{SearchLabelMessage}</StyledInputLabel>
        <SearchButton onClick={handleOpen}>{placeholderMessage}</SearchButton>
      </TopRow>

      <StyledFlexRow>
        <StyledInput
          id="search-input"
          endAdornment={<FilterClearIconButton onClick={handleInputClear} visible={Boolean(search)} />}
          onChange={handleInputChange}
          onKeyDown={handleEscClear}
          placeholder={inputPlaceholder}
          startAdornment={<StyledFilterSearchIcon size={18} />}
          value={value}
        />

        <FilterOptions disabled={disabled} variant="search" onOpenChange={onOpenChange} open={open}>
          <div>
            <MenuContainer>
              {searchFieldExtraOptions.length > 0 && (
                <MenuHeadingTypography size="700-xs" color="gray-80">
                  <FormattedMessage id="searchFilter.menu.headings.defaultsLabel" defaultMessage="Alapértelmezett" />
                </MenuHeadingTypography>
              )}
              {searchFieldOptions.map(option => {
                const isOnlySelected = searchFields.includes(option.key) && searchFields.length === 1
                return (
                  <FilterWidgetFormControlLabel
                    checked={isSingleOptionSearch || searchFields.includes(option.key) || searchFields.length === 0}
                    control={<Checkbox className={cx({ onlySelected: isOnlySelected })} />}
                    disabled={isSingleOptionSearch || isOnlySelected}
                    gap={10}
                    key={option.key}
                    label={option.label}
                    name={option.key}
                    onChange={handleChange}
                  />
                )
              })}
              {searchFieldExtraOptions.length > 0 && (
                <MenuHeadingTypography size="700-xs" color="gray-80">
                  <FormattedMessage id="searchFilter.menu.headings.othersLabel" defaultMessage="További lehetőségek" />
                </MenuHeadingTypography>
              )}
              {searchFieldExtraOptions.map(option => {
                const isOnlySelected = searchFields.includes(option.key) && searchFields.length === 1
                return (
                  <FilterWidgetFormControlLabel
                    checked={isSingleOptionSearch || searchFields.includes(option.key)}
                    control={<Checkbox className={cx({ onlySelected: isOnlySelected })} />}
                    disabled={isSingleOptionSearch || isOnlySelected}
                    gap={10}
                    key={option.key}
                    label={option.label}
                    name={option.key}
                    onChange={handleChange}
                  />
                )
              })}
            </MenuContainer>
            {searchFieldPresets.length > 0 && (
              <StyledMenuList>
                {searchFieldPresets.map(preset => (
                  <ListItem key={preset.key} onClick={handlePresetClick(preset.optionKeys)} menuItem>
                    <Typography color="inherit" size="400-sm">
                      {preset.label}
                    </Typography>
                  </ListItem>
                ))}
              </StyledMenuList>
            )}
          </div>
        </FilterOptions>
      </StyledFlexRow>
    </SearchFilterRoot>
  )
}
